import PopupBasic from 'src/game/components/popups/PopupBasic';
import i18n from 'src/lib/i18n/i18n';
import uiConfig from 'src/lib/ui/config';
import ButtonScaleViewWithText from 'src/lib/ui/components/ButtonScaleViewWithText';
import bitmapFonts from 'src/lib/bitmapFonts';
import View from '@play-co/timestep-core/lib/ui/View';
import {
  CareItem,
  CareItemID,
  CareItemType,
  getCareItems,
} from 'src/replicant/care/care.ruleset';
import { FlexBoxView } from '@play-co/timestep-core/flexbox';
import CareItemButton from '../components/CareItemButton';
import sounds from 'src/lib/sounds';
import * as Pet from 'src/replicant/pet';
import Scroll from '../components/shared/Scroll';
import StateObserver from 'src/StateObserver';
import * as Visit from 'src/replicant/visit';
import { getFriendsPet } from '../pet';
import { Widen } from 'src/replicant/utils/types';
import * as Fate from '../../replicant/fate';
import { getTutorialController } from '../tutorial/TutorialController';
import { registerHandTarget } from '../tutorial/services/TutorialHandService';

import { AssetGroup } from '@play-co/timestep-core';

// Group Care Items in pairs so we can reuse existing scroll components
type CareItemPair = [CareItem, CareItem?];
type CloseHandler = (result?: CareItem['id']) => void;

export default class PopupSelectCareItem extends PopupBasic {
  private careItems: Scroll<CareItemPair>;
  private close: CloseHandler;
  private lastCareItem!: View;

  constructor(opts: { superview: View; close: CloseHandler }) {
    super({
      superview: opts.superview,
      close: () => opts.close(),
      zIndex: 10000,
      width: uiConfig.popups.buruburu.width,
      height: 815,
      darkerBg: false,
      tracking: { feature: 'care', $subFeature: 'popup-careitem' },
      titleIcon: {
        image: 'assets/ui/care/care_firstaid_icon.png',
      },
    });

    this.close = opts.close;
    this.buttonClose.removeFromSuperview();

    this.message.updateOpts({
      width: this.box.style.width - 80,
      height: 50,
      x: 40,
      y: 80,
      zIndex: 1,
    });

    this.careItems = new Scroll<CareItemPair>({
      superview: this.box,
      createItem: this.createItem.bind(this),
      showBg: true,
      rect: {
        x: 30,
        y: 150,
        width: this.box.style.width - 60,
        height: 500,
      },
    });

    new ButtonScaleViewWithText({
      ...uiConfig.buttons.cancel,
      superview: this.box,
      labelOffsetY: -1,
      fontSize: 31,
      font: bitmapFonts('Body'),
      x: this.box.style.width / 2,
      y: this.box.style.height - 45 - 80 / 2,
      width: 259,
      height: 80,
      centerOnOrigin: true,
      localeText: () => i18n('basic.back'),
      onClick: async () => {
        sounds.sfx.play('cancel');
        return opts.close();
      },
      tracking: { feature: 'care', $subFeature: 'popup-careitem-back' },
    });
  }

  protected getAssetsGroup() {
    return new AssetGroup(['assets/ui/care/care_firstaid_icon.png']);
  }

  public init({
    type,
    itemFilter,
  }: {
    type: CareItemType;
    additionalTracking: { itemType: CareItemType };
    itemFilter?: CareItemID;
  }) {
    const stringType = type === 'first_aid' ? 'firstAid' : type;
    super.init({
      title: i18n(`careAction.${stringType}`),
      titleIcon: {
        image: `assets/ui/care/care_${stringType.toLowerCase()}_icon.png`,
        height: stringType === 'snack' ? 75 : 55,
      },
      message: i18n('careItem.selectMessage'),
    });

    this.careItems.setItems(this.getCareItems(type, itemFilter));
    registerHandTarget('lastCareItem', this.lastCareItem);
    void getTutorialController().handleEvent('carePopupOpened');
  }

  /**
   * Return care items list
   */
  private getCareItems(type: CareItemType, itemFilter?: CareItemID) {
    const { user } = StateObserver.getState();
    const now = StateObserver.now();
    const isVisiting = Visit.getters.isVisiting(user);
    const isDying = Fate.getters.getVisiblePetState(user, now) === 'dying';
    const isFriendDying = isVisiting && getFriendsPet()?.state === 'dying';

    // Perfume filter
    // Show only perfume if player pet or friend pet is dying
    const onlyPerfume = (item: CareItem['item']) => {
      if (isDying || isFriendDying) {
        return item === 'perfume' || item === 'superPerfume';
      } else {
        return item !== 'perfume' && item !== 'superPerfume';
      }
    };

    const allItems: Widen<CareItem>[] = getCareItems(
      type,
      Pet.getters.getCurrentEvolution(user)!.stage,
    );

    let filteredItems = allItems.filter(({ item }) => onlyPerfume(item));

    // When itemFilter is specified, restrict to a single care item by id
    if (itemFilter) {
      filteredItems = filteredItems.filter(
        (careItem) => careItem.id === itemFilter,
      );
    }

    const items = filteredItems
      .sort((a, b) => b.priority - a.priority)
      .reduce((result, _, i, array) => {
        if (i % 2 === 0) {
          result.push(array.slice(i, i + 2) as CareItemPair);
        }
        return result;
      }, [] as CareItemPair[]);

    return items;
  }

  private createItem(
    superview: View,
    _: number,
    careItems: [CareItem, CareItem?],
  ) {
    const hasSingleItem = !careItems[1];
    const row = superview.addSubview(
      new FlexBoxView({
        width: superview.style.width,
        height: uiConfig.buruburu.care.itemButton.container.height,
        flex: {
          justifyContent: 'space-evenly',
        },
      }),
    );

    const result = careItems.reduce((container, careItem) => {
      const item = container.addSubview(
        new CareItemButton({
          careItem: careItem!,
          onClick: async () => {
            sounds.sfx.play('ok');
            return this.close(careItem!.id);
          },
        }),
      );
      registerHandTarget(careItem!.item, item);
      this.lastCareItem = item;

      return container;
    }, row);

    if (hasSingleItem) {
      // Keep same horizontal padding as 2-item rows by reserving the 2nd slot.
      result.addSubview(
        new View({
          width: uiConfig.buruburu.care.itemButton.container.width,
          height: uiConfig.buruburu.care.itemButton.container.height,
          opacity: 0,
          canHandleEvents: false,
        }),
      );
    }

    return result;
  }
}
